import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Clock, Eye, Store, Instagram, ExternalLink, Facebook, RefreshCw } from "lucide-react";
import { api, formatApiErrorDetail } from "../../lib/api";

// ---------------------------------------------------------------------------
// Admin → Vendedores → Solicitudes
// Reviewer for vendor_applications with approve/reject modal.
// ---------------------------------------------------------------------------
export default function VendorApplicationsAdmin() {
    const [apps, setApps] = useState([]);
    const [statusFilter, setStatusFilter] = useState("pending");
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/vendor-applications${statusFilter ? `?status=${statusFilter}` : ""}`);
            setApps(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error cargando solicitudes");
        } finally { setLoading(false); }
    }, [statusFilter]);
    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-4" data-testid="vendor-apps-admin">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="inline-flex items-center gap-1">
                    {[
                        { id: "pending", label: "En revisión", icon: Clock, cls: "text-amber-300" },
                        { id: "approved", label: "Aprobadas", icon: CheckCircle2, cls: "text-emerald-300" },
                        { id: "rejected", label: "Rechazadas", icon: XCircle, cls: "text-rose-300" },
                        { id: "", label: "Todas", icon: null, cls: "" },
                    ].map((f) => (
                        <button key={f.id || "all"} onClick={() => setStatusFilter(f.id)}
                            className={`text-xs px-3 py-1.5 rounded border inline-flex items-center gap-1 transition ${statusFilter === f.id
                                ? "border-[var(--tb-gold)] bg-[var(--tb-gold)]/10 text-[var(--tb-gold)]"
                                : "border-[var(--tb-border)] text-[var(--tb-text-2)] hover:border-[var(--tb-gold)]/50"}`}
                            data-testid={`vendor-apps-filter-${f.id || "all"}`}>
                            {f.icon && <f.icon className={`w-3.5 h-3.5 ${f.cls}`} />}
                            {f.label}
                        </button>
                    ))}
                </div>
                <button onClick={load} className="tb-btn-ghost !text-xs inline-flex items-center gap-1">
                    <RefreshCw className="w-3.5 h-3.5" /> Refrescar
                </button>
            </div>

            {loading ? (
                <div className="text-xs text-[var(--tb-muted)] text-center py-6">Cargando…</div>
            ) : apps.length === 0 ? (
                <div className="tb-card p-6 text-center text-xs text-[var(--tb-muted)]">
                    <Store className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    No hay solicitudes {statusFilter && `con estado "${statusFilter}"`}.
                </div>
            ) : (
                <div className="space-y-2">
                    {apps.map((app) => (
                        <div key={app.id} className="tb-card p-4 flex items-start gap-3" data-testid={`vendor-app-${app.id}`}>
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap mb-1">
                                    <Store className="w-4 h-4 text-[var(--tb-gold)] flex-shrink-0" />
                                    <div className="font-display text-base truncate">{app.store_name}</div>
                                    <StatusBadge status={app.status} />
                                </div>
                                <div className="text-xs text-[var(--tb-text-2)] truncate">
                                    {app.user_name} · <span className="text-[var(--tb-muted)]">{app.user_email}</span>
                                    {" · "}<span className="capitalize">{app.user_current_role || "cliente"}</span>
                                </div>
                                <div className="text-xs text-[var(--tb-text-2)] mt-1 line-clamp-2 italic">
                                    &ldquo;{app.store_description}&rdquo;
                                </div>
                                {app.categories && app.categories.length > 0 && (
                                    <div className="mt-1 flex flex-wrap gap-1">
                                        {app.categories.slice(0, 5).map((c) => (
                                            <span key={c} className="text-[9px] uppercase tracking-wider bg-[var(--tb-gold)]/10 text-[var(--tb-gold)] px-1.5 py-0.5 rounded">{c}</span>
                                        ))}
                                    </div>
                                )}
                                <div className="text-[10px] text-[var(--tb-muted)] mt-1">
                                    {app.created_at ? new Date(app.created_at).toLocaleString("es-SV") : ""}
                                </div>
                            </div>
                            <button onClick={() => setSelected(app)} className="tb-btn-secondary !text-xs inline-flex items-center gap-1 flex-shrink-0" data-testid={`vendor-app-review-${app.id}`}>
                                <Eye className="w-3.5 h-3.5" /> Revisar
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {selected && (
                <ReviewVendorApplicationModal
                    app={selected}
                    onClose={() => setSelected(null)}
                    onDone={() => { setSelected(null); load(); }}
                />
            )}
        </div>
    );
}

function StatusBadge({ status }) {
    const cfg = {
        pending: { cls: "bg-amber-500/20 text-amber-300 border-amber-400/40", label: "En revisión" },
        approved: { cls: "bg-emerald-500/20 text-emerald-300 border-emerald-400/40", label: "Aprobada" },
        rejected: { cls: "bg-rose-500/20 text-rose-300 border-rose-400/40", label: "Rechazada" },
    }[status] || { cls: "bg-[var(--tb-bg-2)] text-[var(--tb-muted)] border-[var(--tb-border)]", label: status };
    return <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${cfg.cls}`}>{cfg.label}</span>;
}

function ReviewVendorApplicationModal({ app, onClose, onDone }) {
    const [billingModel, setBillingModel] = useState("commission");
    const [commission, setCommission] = useState(15);
    const [subscription, setSubscription] = useState(20);
    const [adminNote, setAdminNote] = useState("");
    const [rejectMode, setRejectMode] = useState(false);
    const [saving, setSaving] = useState(false);
    const [deptoName, setDeptoName] = useState("");
    const [muniName, setMuniName] = useState("");

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!app.departamento_id) return;
            try {
                const { data: deptos } = await api.get("/c807-public/catalogo/departamentos");
                const dlist = Array.isArray(deptos) ? deptos : (deptos?.items || []);
                const d = dlist.find((x) => String(x.id) === String(app.departamento_id));
                if (!cancelled && d) setDeptoName(d.nombre);
                if (app.municipio_id) {
                    const { data: munis } = await api.get(`/c807-public/catalogo/municipios?departamento=${app.departamento_id}`);
                    const mlist = Array.isArray(munis) ? munis : (munis?.items || []);
                    const m = mlist.find((x) => String(x.id) === String(app.municipio_id));
                    if (!cancelled && m) setMuniName(m.nombre);
                }
            } catch { /* ignore */ }
        })();
        return () => { cancelled = true; };
    }, [app.departamento_id, app.municipio_id]);

    const approve = async () => {
        if (billingModel === "commission" && (!commission || commission <= 0)) {
            toast.error("Indica un % de comisión mayor a 0"); return;
        }
        if (billingModel === "subscription" && (!subscription || subscription <= 0)) {
            toast.error("Indica un precio de suscripción mayor a 0"); return;
        }
        setSaving(true);
        try {
            await api.post(`/admin/vendor-applications/${app.id}/approve`, {
                billing_model: billingModel,
                commission_percent: billingModel === "commission" ? commission : 0,
                subscription_price: billingModel === "subscription" ? subscription : 0,
                admin_note: adminNote,
            });
            toast.success(`${app.user_name} ahora es vendedor`);
            onDone();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error aprobando");
        } finally { setSaving(false); }
    };

    const reject = async () => {
        if (adminNote.trim().length < 5) {
            toast.error("Escribe el motivo del rechazo (mín 5 caracteres)"); return;
        }
        setSaving(true);
        try {
            await api.post(`/admin/vendor-applications/${app.id}/reject`, { admin_note: adminNote });
            toast.success("Solicitud rechazada");
            onDone();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error rechazando");
        } finally { setSaving(false); }
    };

    const canApprove = app.status === "pending";
    return (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
            <div className="tb-card p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="vendor-app-modal">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <h3 className="font-display text-xl inline-flex items-center gap-2">
                            <Store className="w-5 h-5 text-[var(--tb-gold)]" />
                            {app.store_name}
                        </h3>
                        <div className="text-xs text-[var(--tb-muted)] mt-1">{app.user_name} · {app.user_email}</div>
                    </div>
                    <button onClick={onClose} className="tb-btn-ghost !p-1"><XCircle className="w-5 h-5" /></button>
                </div>

                <div className="space-y-3 text-sm">
                    <div>
                        <div className="tb-overline text-[var(--tb-gold)] text-[10px]">Qué vende</div>
                        <div className="mt-1">{app.store_description}</div>
                    </div>
                    {app.categories?.length > 0 && (
                        <div>
                            <div className="tb-overline text-[var(--tb-gold)] text-[10px]">Categorías</div>
                            <div className="mt-1 flex flex-wrap gap-1">
                                {app.categories.map((c) => (
                                    <span key={c} className="text-[10px] uppercase bg-[var(--tb-gold)]/10 text-[var(--tb-gold)] px-2 py-0.5 rounded">{c}</span>
                                ))}
                            </div>
                        </div>
                    )}
                    {(app.previous_experience || app.expected_monthly_volume) && (
                        <div className="grid sm:grid-cols-2 gap-3 pt-2 border-t border-[var(--tb-border)]">
                            {app.previous_experience && <div>
                                <div className="tb-overline text-[10px]">Experiencia</div>
                                <div>{app.previous_experience}</div>
                            </div>}
                            {app.expected_monthly_volume && <div>
                                <div className="tb-overline text-[10px]">Volumen esperado</div>
                                <div>{app.expected_monthly_volume}</div>
                            </div>}
                        </div>
                    )}
                    {app.motivation && (
                        <div>
                            <div className="tb-overline text-[10px]">Motivación</div>
                            <div className="italic">&ldquo;{app.motivation}&rdquo;</div>
                        </div>
                    )}
                    {app.sample_photos?.length > 0 && (
                        <div>
                            <div className="tb-overline text-[10px] mb-1">Fotos de muestra</div>
                            <div className="grid grid-cols-3 gap-2">
                                {app.sample_photos.map((url, i) => (
                                    <a key={i} href={url} target="_blank" rel="noreferrer" className="block">
                                        <img src={url} alt="" className="w-full h-20 object-cover rounded border border-[var(--tb-border)] hover:opacity-80" />
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}
                    {app.contact_phone && <div className="text-xs">📞 {app.contact_phone}</div>}
                    {(app.social_links?.instagram || app.social_links?.tiktok || app.social_links?.facebook) && (
                        <div className="flex gap-2 text-xs">
                            {app.social_links.instagram && <span className="inline-flex items-center gap-1"><Instagram className="w-3 h-3" /> {app.social_links.instagram}</span>}
                            {app.social_links.tiktok && <span className="inline-flex items-center gap-1"><ExternalLink className="w-3 h-3" /> {app.social_links.tiktok}</span>}
                            {app.social_links.facebook && <span className="inline-flex items-center gap-1"><Facebook className="w-3 h-3" /> {app.social_links.facebook}</span>}
                        </div>
                    )}

                    {(app.address_street || app.departamento_id) && (
                        <div className="pt-2 border-t border-[var(--tb-border)] bg-[var(--tb-bg)]/50 p-2 rounded">
                            <div className="text-[10px] text-[var(--tb-gold)] uppercase tracking-wider mb-1">🔒 Dirección</div>
                            <div className="text-xs space-y-0.5">
                                {app.address_street && <div>{app.address_street}</div>}
                                {(deptoName || muniName) && <div>{[muniName, deptoName].filter(Boolean).join(", ")}, El Salvador</div>}
                                {app.address_reference && <div className="text-[var(--tb-muted)] italic">Ref: {app.address_reference}</div>}
                            </div>
                        </div>
                    )}
                    {(app.document_type || app.document_number) && (
                        <div className="pt-2 border-t border-[var(--tb-border)] bg-[var(--tb-bg)]/50 p-2 rounded">
                            <div className="text-[10px] text-[var(--tb-gold)] uppercase tracking-wider mb-1">🔒 Documento</div>
                            <div className="text-xs"><strong>{app.document_type}:</strong> <span className="font-mono">{app.document_number}</span></div>
                        </div>
                    )}
                    {(app.bank_name || app.bank_account_number) && (
                        <div className="pt-2 border-t border-[var(--tb-border)] bg-[var(--tb-bg)]/50 p-2 rounded">
                            <div className="text-[10px] text-[var(--tb-gold)] uppercase tracking-wider mb-1">🔒 Datos bancarios</div>
                            <div className="text-xs space-y-0.5">
                                {app.bank_name && <div>Banco: <strong>{app.bank_name}</strong></div>}
                                {app.bank_account_type && <div>Tipo: {app.bank_account_type}</div>}
                                {app.bank_account_number && <div className="font-mono">Cuenta: {app.bank_account_number}</div>}
                                {app.bank_account_holder && <div>Titular: {app.bank_account_holder}</div>}
                            </div>
                        </div>
                    )}
                </div>

                {canApprove && (
                    <div className="mt-5 pt-4 border-t border-[var(--tb-border)] space-y-3">
                        {!rejectMode ? (
                            <>
                                <h4 className="font-display text-sm">Configurar aprobación</h4>
                                <div>
                                    <div className="tb-overline text-[10px] mb-1">Modelo de cobro</div>
                                    <div className="flex gap-2">
                                        <button type="button" onClick={() => setBillingModel("commission")}
                                            className={`flex-1 text-xs px-3 py-2 rounded border ${billingModel === "commission" ? "border-[var(--tb-gold)] bg-[var(--tb-gold)]/10 text-[var(--tb-gold)]" : "border-[var(--tb-border)] text-[var(--tb-text-2)]"}`}
                                            data-testid="vendor-app-model-commission">
                                            Comisión por venta
                                        </button>
                                        <button type="button" onClick={() => setBillingModel("subscription")}
                                            className={`flex-1 text-xs px-3 py-2 rounded border ${billingModel === "subscription" ? "border-[var(--tb-gold)] bg-[var(--tb-gold)]/10 text-[var(--tb-gold)]" : "border-[var(--tb-border)] text-[var(--tb-text-2)]"}`}
                                            data-testid="vendor-app-model-subscription">
                                            Suscripción mensual
                                        </button>
                                    </div>
                                </div>
                                {billingModel === "commission" ? (
                                    <div>
                                        <label className="tb-overline text-[10px]">% de comisión sobre cada venta</label>
                                        <input type="number" min={0} max={100} step={0.5} className="tb-input mt-1" value={commission}
                                            onChange={(e) => setCommission(Number(e.target.value))} data-testid="vendor-app-commission" />
                                    </div>
                                ) : (
                                    <div>
                                        <label className="tb-overline text-[10px]">Precio suscripción (USD/mes)</label>
                                        <input type="number" min={0} step={0.5} className="tb-input mt-1" value={subscription}
                                            onChange={(e) => setSubscription(Number(e.target.value))} data-testid="vendor-app-subscription" />
                                    </div>
                                )}
                                <div>
                                    <label className="tb-overline text-[10px]">Nota (opcional, se envía por email)</label>
                                    <textarea className="tb-input mt-1 min-h-[50px]" value={adminNote} onChange={(e) => setAdminNote(e.target.value)}
                                        placeholder="Bienvenido al equipo! ..." data-testid="vendor-app-note" />
                                </div>
                                <div className="flex gap-2 justify-end pt-2">
                                    <button onClick={() => setRejectMode(true)} className="tb-btn-ghost !text-xs !text-rose-400" data-testid="vendor-app-reject-btn">
                                        Rechazar
                                    </button>
                                    <button onClick={approve} disabled={saving}
                                        className="tb-btn-primary !text-xs inline-flex items-center gap-1 disabled:opacity-50"
                                        data-testid="vendor-app-approve">
                                        <CheckCircle2 className="w-3.5 h-3.5" /> {saving ? "Aprobando…" : "Aprobar"}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <h4 className="font-display text-sm text-rose-400">Rechazar solicitud</h4>
                                <textarea className="tb-input min-h-[80px]" value={adminNote} onChange={(e) => setAdminNote(e.target.value)}
                                    placeholder="Motivo del rechazo (mínimo 5 caracteres, se envía por email al aplicante)"
                                    data-testid="vendor-app-reject-note" />
                                <div className="flex gap-2 justify-end pt-2">
                                    <button onClick={() => setRejectMode(false)} className="tb-btn-ghost !text-xs">
                                        Volver
                                    </button>
                                    <button onClick={reject} disabled={saving}
                                        className="!text-xs px-4 py-2 rounded bg-rose-600 hover:bg-rose-700 text-white inline-flex items-center gap-1 disabled:opacity-50"
                                        data-testid="vendor-app-reject-confirm">
                                        <XCircle className="w-3.5 h-3.5" /> {saving ? "Rechazando…" : "Confirmar rechazo"}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {!canApprove && app.admin_note && (
                    <div className="mt-4 pt-4 border-t border-[var(--tb-border)]">
                        <div className="tb-overline text-[10px]">Nota del equipo</div>
                        <div className="text-xs mt-1 italic">&ldquo;{app.admin_note}&rdquo;</div>
                    </div>
                )}
            </div>
        </div>
    );
}
